import { brand } from "../../../../config/theme";
import { useState, useRef, useEffect, memo, useMemo } from "react";
import Tooltip from "../../../../components/ui/Tooltip";
import { useTranslation } from "react-i18next";
import {
  useVehiclesSocketStore,
  type VehicleSocket,
} from "../../../../store/admin/vehiclesSocketStore";
import {
  useActiveTripsStore,
  getActiveTripForUnit,
} from "../../../../store/admin/activeTripsStore";
import { useTripTrackingStore } from "../../../../store/admin/tripTrackingStore";
import type { TripTrackingEvent } from "../../../../socket";

const EMPTY_TRIP_EVENTS: TripTrackingEvent[] = [];
import { getTimeAgo, type TimeTranslations } from "../../../../utils/getDate";
import { shortAgo, unitKindLine } from "./compactRow";

import SosIcon from "../../icons/biggerIcons/SosIcon";
import BatteryIcon from "../../icons/biggerIcons/BatteryIcon";
import SignIcon from "../../icons/biggerIcons/SignIcon";
import LastReportIcon from "../../icons/biggerIcons/LastReportIcon";
import IgnitionIcon from "../../icons/biggerIcons/IgnitionIcon";

import ShareIcon from "../../icons/littleIcons/ShareIcon";
import RouteIcon from "../../icons/littleIcons/RouteIcon";
import FootprintUnitIcon from "../../icons/littleIcons/FootprintUnitIcon";
import DriverIcon from "../../icons/littleIcons/DriverIcon";
import SettingsIcon from "../../icons/littleIcons/Settings";
import TrailerIcon from "../../icons/littleIcons/Trailer";
import EventsIcon from "../../icons/littleIcons/EventsIcon";
import StatesIcon from "../../icons/littleIcons/StatesIcon";
import ProfilteIcon from "../../icons/littleIcons/ProfilteIcon";
import RecorderIcon from "../../icons/littleIcons/RecorderIcon";
import GeneralInformationIcon from "../../icons/littleIcons/GenerarlInformationIcon";

import {
  GeneralInformationContent,
  DriverContent,
  ActionsContent,
  TrailerContent,
  EventsContent,
  StatesContent,
  UnitsContent,
  FootprintContent,
  RouteContent,
  ShareContent,
  MediaRecorderContent,
} from "./optionsIcons";
import TripHistoryModal from "./optionsIcons/modals/TripHistoryModal";
import VehicleEvidenceModal from "./optionsIcons/modals/VehicleEvidenceModal";
import VehicleLiveDataModal from "./optionsIcons/modals/VehicleLiveDataModal";
import UnitBell from "./UnitBell";
import OdometerIcon from "../../../../pages/home/components/icons/Odometer";
import { liveSpeedKmh, readEngine } from "./engineDisplay";
import { TONE_BORDER, TONE_COLOR, batteryTone, connectionTone, gpsTone, gradeTone } from "./indicators";
import { startOfToday, useTodayDrivingScore } from "../../../../features/behavior/useTodayDrivingScore";
import { fmtDuration, fmtKm, todaySummary } from "../../../../features/behavior/todaySummary";
import { gradeOf } from "../../../../pages/reports/utils/drivingScore";
import { useNavigate } from "react-router-dom";
import SpeedIcon from "../../../../pages/home/components/icons/Speed";
import { useIsWebServiceRole } from "../../../../hooks/useIsWebServiceRole";
import { useCopyLocation } from "../../../../hooks/useCopyLocation";
import UnitVehicleAvatar from "../../../../components/vehicles/UnitVehicleAvatar";
import { useIsUserDev } from "../../../../hooks/useIsUserDev";
import { useDriveView } from "../../../../features/driveView/driveViewStore";
import { canDrawDriveView } from "../../../../features/driveView/webgl";
import { useLiveDataPanelStore } from "../../../../store/lite/liveDataPanelStore";
import { useCompanyIntegratorsStore } from "../../../../store/admin/companyIntegratorsStore";
import { matchesModel, HITCH_MODELS, ENGINE_CUTOFF_BRANDS } from "../../../../utils/vehicleParamsResolver";
import BitacoraDrawer from "../../../../pages/quick-reports/bitacora/BitacoraDrawer";
import { UNIT_STATUS_COLORS as STATUS_COLORS } from "../../../../constants/unitColors";

const HOVER_GREEN = brand.primary;
const GRAY_COLOR = "#9CA3AF";
const RED_COLOR = "#EF4444";
const YELLOW_COLOR = "#F59E0B";
const GREEN_COLOR = "#22C55E";

type ActionIconType =
  | "share"
  | "route"
  | "footprint"
  | "driver"
  | "settings"
  | "trailer"
  | "events"
  | "states"
  | "units"
  | "record"
  | "generalInformation"
  | "drivingEvents";

// Ícono de "Eventos de conducción" (la bitácora de jornada): mismo contrato que los SVG del resto.
const DrivingEventsIcon = ({ color }: { color?: string }) => (
  <i className="ri-steering-2-line text-sm leading-none" style={{ color }} />
);

// =============== FUNCIONES DE COLOR PARA ICONOS GRANDES ===============

const PANIC_CODES = new Set<number>([5]);

const isPanicCode = (panic: number | null | undefined): boolean =>
  panic != null && PANIC_CODES.has(Number(panic));

const getPanicStatusColor = (panic: number | null | undefined): string => {
  if (isPanicCode(panic)) return RED_COLOR;
  return GRAY_COLOR;
};

const getPanicStatusBorderColor = (
  panic: number | null | undefined,
): string => {
  if (isPanicCode(panic)) return "border-[#EF4444]";
  return "border-gray-500";
};

// 2. Engine Status: la decisión vive en engineDisplay.ts (pura y con tests).
interface EngineDisplayInfo {
  color: string;
  borderColor: string;
  isOn: boolean | null; // true = ON, false = OFF, null = sin datos
  lastKnownOn: boolean | null;
  stale: boolean;
}

const getEngineDisplayInfo = (
  speed: number | null | undefined,
  status: string,
  engineStatus: number | null | undefined,
): EngineDisplayInfo => {
  const { tone, lastKnownOn } = readEngine(speed, status, engineStatus);
  const stale = status === "offline";
  switch (tone) {
    case "on":
      return { color: GREEN_COLOR, borderColor: "border-[#22C55E]", isOn: true, lastKnownOn, stale };
    case "off":
      return { color: RED_COLOR, borderColor: "border-[#EF4444]", isOn: false, lastKnownOn, stale };
    case "unknown":
      return { color: YELLOW_COLOR, borderColor: "border-[#F59E0B]", isOn: null, lastKnownOn, stale };
    default:
      return { color: GRAY_COLOR, borderColor: "border-gray-500", isOn: null, lastKnownOn, stale };
  }
};

// 3-5. Batería, GPS y última conexión: umbrales en indicators.ts (compartidos
// con la fila compacta).
const getBatteryColor = (battery: number | null | undefined): string =>
  TONE_COLOR[batteryTone(battery)];
const getBatteryBorderColor = (battery: number | null | undefined): string =>
  TONE_BORDER[batteryTone(battery)];
const getGpsSatColor = (gpsSat: number | null | undefined): string =>
  TONE_COLOR[gpsTone(gpsSat)];
const getGpsSatBorderColor = (gpsSat: number | null | undefined): string =>
  TONE_BORDER[gpsTone(gpsSat)];
const getLastConnectionColor = (timestamp: number | null | undefined): string =>
  TONE_COLOR[connectionTone(timestamp, Date.now())];
const getLastConnectionBorderColor = (timestamp: number | null | undefined): string =>
  TONE_BORDER[connectionTone(timestamp, Date.now())];

// 6. Odometer - Siempre gris
const formatOdometer = (odometer: number | null | undefined, noDataText: string): string => {
  if (odometer === null || odometer === undefined) return noDataText;
  return `${Math.round(odometer).toLocaleString()} km`;
};

// =============== FIN FUNCIONES DE COLOR ===============

interface VehicleCardProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  themeClasses: {
    cardBg: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
  };
  selectedStatus?: string | null;
  onFocus?: () => void;
}

// Componente Skeleton para cards cargando posición
const PositionLoadingSkeleton = ({
  isDarkMode,
  themeClasses,
  vehicleName,
}: {
  isDarkMode: boolean;
  themeClasses: VehicleCardProps["themeClasses"];
  vehicleName: string;
}) => (
  <div
    className={`p-3 ${themeClasses.cardBg} rounded-lg border ${themeClasses.cardBorder} animate-pulse`}
  >
    <div className="flex items-center gap-2 mb-3">
      <div
        className={`w-5 h-5 rounded ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
      ></div>
      <div
        className={`w-5 h-5 rounded ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
      ></div>
      <span className={`font-mono font-bold text-sm ${themeClasses.text}`}>
        {vehicleName}
      </span>
    </div>
    <div className="flex items-center justify-evenly gap-1 mb-3">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
        <div key={i} className="flex flex-col items-center flex-1">
          <div
            className={`w-6 h-6 rounded ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
          ></div>
          <div
            className={`w-8 h-2 mt-2 rounded ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
          ></div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-center gap-2">
      <div
        className={`animate-spin h-4 w-4 border-2 border-t-transparent rounded-full ${isDarkMode ? "border-gray-400" : "border-gray-500"}`}
      ></div>
      <span className={`text-xs ${themeClasses.textSecondary}`}>
        Obteniendo posición...
      </span>
    </div>
  </div>
);

function VehicleCardComponent({
  vehicle,
  isDarkMode,
  themeClasses,
  selectedStatus,
  onFocus,
}: VehicleCardProps) {
  const { t } = useTranslation("lite");
  const setFocusedVehicleId = useVehiclesSocketStore(
    (state) => state.setFocusedVehicleId,
  );
  const highlightedVehicleId = useVehiclesSocketStore(
    (state) => state.highlightedVehicleId,
  );
  const requestAddressForDevice = useVehiclesSocketStore(
    (state) => state.requestAddressForDevice,
  );
  const timeTick = useVehiclesSocketStore((state) => state.timeTick);

  // Obtener datos frescos del store (actualizados por socket) - O(1) via Map
  const vehicleFromStore = useVehiclesSocketStore((state) => {
    const idx = state.vehiclesMap.get(vehicle.id);
    return idx !== undefined ? state.vehicles[idx] : undefined;
  });

  // Usar datos del store si están disponibles, sino usar el prop
  const currentVehicle = vehicleFromStore || vehicle;

  // Verificar si este vehículo está cargando posición (O(1) lookup)
  const isLoadingPosition = useVehiclesSocketStore(
    (state) => !!state.loadingPositionDeviceIds[vehicle.id],
  );

  // Active trip assigned to this vehicle (only if status is "selected" / in progress)
  const activeTrip = useActiveTripsStore((state) =>
    getActiveTripForUnit(vehicle.vehicle_id, vehicle.remolque_id, vehicle.id, state),
  );

  // Real-time trip tracking state from the geofence worker
  const tripTracking = useTripTrackingStore(
    (state) => state.byDeviceId[vehicle.id] || null,
  );
  const tripEvents = useTripTrackingStore(
    (state) => state.eventsByDevice[vehicle.id] ?? EMPTY_TRIP_EVENTS,
  );
  const [showTripHistory, setShowTripHistory] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showBitacora, setShowBitacora] = useState(false);
  const { isWebServiceRole } = useIsWebServiceRole();
  const toggleDriveView = useDriveView((s) => s.toggle);
  const driveViewOpen = useDriveView((s) => s.vehicleId === vehicle.id);
  const driveViewTried = useDriveView((s) => s.tried);
  const { isUserDev } = useIsUserDev();
  const isLiveDataOpen = useLiveDataPanelStore(
    (state) => state.deviceId === vehicle.id,
  );
  const openLiveDataPanel = useLiveDataPanelStore((state) => state.openLiveDataPanel);
  const closeLiveDataPanel = useLiveDataPanelStore((state) => state.closeLiveDataPanel);
  const coversAllDevices = useCompanyIntegratorsStore(
    (state) => state.coversAllDevices,
  );
  const coveredDeviceIds = useCompanyIntegratorsStore(
    (state) => state.coveredDeviceIds,
  );
  const isEvidenceAvailable =
    coversAllDevices || coveredDeviceIds.has(vehicle.id);

  // Verificar si este vehículo está resaltado (viene de remolques)
  const isHighlighted = highlightedVehicleId === vehicle.id;
  // La unidad seguida en el mapa: marcada aquí también (ver VehicleRow).
  const isSelected = useVehiclesSocketStore((s) => s.focusedVehicleId === vehicle.id);
  const navigate = useNavigate();
  // Sólo la unidad seleccionada: en densidad «detallada» hay una tarjeta por unidad.
  const todayScore = useTodayDrivingScore(vehicle.id, isSelected);
  const todayGrade = gradeOf(todayScore.data?.overall ?? null);
  const todayTone = gradeTone(todayGrade);
  const cardRef = useRef<HTMLDivElement>(null);
  const [hoveredIcon, setHoveredIcon] = useState<ActionIconType | null>(null);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const copyLocation = useCopyLocation(currentVehicle.latitude, currentVehicle.longitude);
  const [activeActionIcon, setActiveActionIcon] =
    useState<ActionIconType | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const liveSpeed = liveSpeedKmh(currentVehicle.speed, currentVehicle.status);

  // Memoizar cálculos de colores usando datos frescos del store
  const {
    panicColor,
    panicBorderColor,
    engineDisplayInfo,
    batteryColor,
    batteryBorderColor,
    gpsSatColor,
    gpsSatBorderColor,
    lastConnectionColor,
    lastConnectionBorderColor,
    odometerText,
  } = useMemo(
    () => ({
      panicColor: getPanicStatusColor(currentVehicle.params?.panic),
      panicBorderColor: getPanicStatusBorderColor(currentVehicle.params?.panic),
      // Nueva lógica: speed > 0 → ON, movimiento real → ON, sino → engine_status
      engineDisplayInfo: getEngineDisplayInfo(
        currentVehicle.speed,
        currentVehicle.status,
        currentVehicle.engineStatus,
      ),
      batteryColor: getBatteryColor(currentVehicle.battery),
      batteryBorderColor: getBatteryBorderColor(currentVehicle.battery),
      gpsSatColor: getGpsSatColor(currentVehicle.gps_sat),
      gpsSatBorderColor: getGpsSatBorderColor(currentVehicle.gps_sat),
      lastConnectionColor: getLastConnectionColor(
        currentVehicle.lastReport._seconds,
      ),
      lastConnectionBorderColor: getLastConnectionBorderColor(
        currentVehicle.lastReport._seconds,
      ),
      odometerText: formatOdometer(currentVehicle.odometer, t("vehicleCard.noData")),
    }),
    [
      currentVehicle.params?.panic,
      currentVehicle.speed,
      currentVehicle.status,
      currentVehicle.engineStatus,
      currentVehicle.battery,
      currentVehicle.gps_sat,
      currentVehicle.lastReport._seconds,
      currentVehicle.odometer,
    ],
  );

  const isHitchModel = useMemo(
    () => matchesModel(currentVehicle.brandModel, HITCH_MODELS),
    [currentVehicle.brandModel],
  );

  const isEngineCutoffBrand = useMemo(
    () => matchesModel(currentVehicle.brandName, ENGINE_CUTOFF_BRANDS),
    [currentVehicle.brandName],
  );

  // Traducciones para el tiempo
  const timeTranslations: TimeTranslations = useMemo(() => ({
    noData: t("time.noData"),
    rightNow: t("time.rightNow"),
    min: t("time.min"),
    hour: t("time.hour"),
    hours: t("time.hours"),
    day: t("time.day"),
    days: t("time.days"),
    month: t("time.month"),
    months: t("time.months"),
    and: t("time.and"),
  }), [t]);

  const lastConnectionText = useMemo(
    () => getTimeAgo(currentVehicle.lastReport._seconds, timeTranslations),
    [currentVehicle.lastReport._seconds, timeTranslations, timeTick],
  );
  // La celda mide ~1/5 de la tarjeta: «2 horas y 41 min» salía «2 horas y…».
  // Ahí va la forma corta de la fila compacta; la larga queda en el title.
  const lastConnectionShort = useMemo(
    () => shortAgo(currentVehicle.lastReport._seconds, Date.now(), t("compactList.now")),
    [currentVehicle.lastReport._seconds, t, timeTick],
  );

  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isHighlighted]);
  useEffect(() => {
    if (isSelected) cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [isSelected]);

  useEffect(() => {
    if (contentRef.current && activeActionIcon) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });

      resizeObserver.observe(contentRef.current);

      requestAnimationFrame(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });

      return () => resizeObserver.disconnect();
    } else {
      setContentHeight(0);
    }
  }, [activeActionIcon]);

  // Si está cargando posición, mostrar skeleton
  if (isLoadingPosition) {
    return (
      <PositionLoadingSkeleton
        isDarkMode={isDarkMode}
        themeClasses={themeClasses}
        vehicleName={vehicle.vehicle?.name || vehicle.imei || vehicle.id}
      />
    );
  }

  const highlightedBg = isDarkMode ? "bg-[#2D4A1C]" : "bg-[#D4FFB8]";
  const cardBackground = isHighlighted ? highlightedBg : themeClasses.cardBg;

  // Obtener el color del borde basado en el estado del vehículo cuando hay filtro activo
  const getStatusBorderColor = () => {
    if (isHighlighted) return brand.primary;
    if (selectedStatus && currentVehicle.status) {
      return STATUS_COLORS[currentVehicle.status] || undefined;
    }
    return undefined;
  };

  const statusBorderColor = getStatusBorderColor();
  const selectedBorder = isHighlighted
    ? "border-2"
    : selectedStatus
      ? "border-2"
      : themeClasses.cardBorder;

  const highlightAnimation = isHighlighted
    ? "animate-pulse ring-2 ring-primary-500 ring-opacity-75"
    : isSelected
      ? "ring-2 ring-primary-500/70"
      : "";

  const handleCardClick = (e: React.MouseEvent) => {
    setFocusedVehicleId(vehicle.id);
    const fromControl = (e.target as HTMLElement).closest("button, input, a");
    !fromControl && onFocus?.();
  };

  const handleActionIconClick = (iconName: ActionIconType) => {
    if (isWebServiceRole && iconName !== "generalInformation") return;
    const isOpening = activeActionIcon !== iconName;
    setActiveActionIcon((prev) => (prev === iconName ? null : iconName));

    // Enfocar el vehículo en el mapa cuando se abre una opción
    if (isOpening) {
      setFocusedVehicleId(vehicle.id);
    }
  };

  const getIconColor = (iconName: ActionIconType) => {
    if (activeActionIcon === iconName) return "#000000";
    if (hoveredIcon === iconName) return HOVER_GREEN;
    return GRAY_COLOR;
  };

  // Función para obtener el color del texto basado en el color del icono
  const getTextColorClass = (color: string): string => {
    if (color === GREEN_COLOR) return "text-[#22C55E]";
    if (color === RED_COLOR) return "text-[#EF4444]";
    if (color === YELLOW_COLOR) return "text-[#F59E0B]";
    return themeClasses.textSecondary;
  };

  const renderOptionContent = () => {
    switch (activeActionIcon) {
      case "generalInformation":
        return <GeneralInformationContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "driver":
        return <DriverContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "settings":
        return <ActionsContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "trailer":
        return <TrailerContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "events":
        return <EventsContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "states":
        return <StatesContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "units":
        return <UnitsContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "footprint":
        return <FootprintContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "route":
        return <RouteContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "share":
        return <ShareContent vehicle={currentVehicle} isDarkMode={isDarkMode} />;
      case "record":
        return (
          <MediaRecorderContent
            vehicle={currentVehicle}
            isDarkMode={isDarkMode}
            onClose={() => setActiveActionIcon(null)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      className={`p-3 ${cardBackground} rounded-lg border ${selectedBorder} transition-all duration-200 ${highlightAnimation} relative cursor-pointer`}
      style={statusBorderColor ? { borderColor: statusBorderColor } : undefined}
    >
      {isHighlighted && (
        <div className="absolute -top-2 -right-2 bg-primary-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg z-10 flex items-center gap-1">
          <i className="ri-truck-line text-xs"></i>
          Remolque
        </div>
      )}
      {/* Carátula o, con "Clásico", una losa del mismo tamaño: la columna
          queda alineada con "Ruta de hoy"/"Ubicación" en las dos. */}
      <div className="flex items-center gap-2 mb-1 min-w-0">
        {/* La carátula del vehículo en vez de un camión genérico. Con
            "Clásico" también lleva lápiz: sin él, quien elegía el clásico
            ya no podía volver a cambiarlo desde aquí. */}
        <UnitVehicleAvatar
          vehicleId={currentVehicle.vehicle_id ?? ""}
          name={currentVehicle.vehicle?.name || currentVehicle.imei || currentVehicle.id}
          pointerModel={currentVehicle.vehicle?.pointerModel}
          bodyType={currentVehicle.vehicle?.bodyType}
          unitType={currentVehicle.vehicle?.type}
          editable={!!currentVehicle.vehicle_id && !isWebServiceRole}
          className="-my-1"
          fallback={<span className={`w-16 h-11 rounded-lg flex items-center justify-center ring-1 ring-inset ${isDarkMode ? "bg-white/[0.04] ring-white/5" : "bg-gray-50 ring-black/5"}`}><i className="ri-truck-line text-lg text-gray-400" /></span>}
        />

        <span
          // min-w-0 + truncate: con shrink-0 un nombre de 45 caracteres
          // («TRACTOCAMION KENWORTH T680 ECO 12 RUTA MONTERREY») salía del
          // borde de la tarjeta, sacaba la campana y metía scroll horizontal
          // a toda la lista.
          className={`min-w-0 truncate font-mono font-bold text-sm ${themeClasses.text}`}
          title={vehicle.vehicle?.name || vehicle.imei || vehicle.id}
        >
          {vehicle.vehicle?.name || vehicle.imei || vehicle.id}
        </span>

        {/* Tipo y modelo en una línea gris (unitKindLine). */}
        <span
          className={`min-w-0 truncate text-[11px] ${themeClasses.textSecondary}`}
          title={currentVehicle.device?.type ? `${t("vehicleCard.deviceLabel")}: ${currentVehicle.device.type}` : undefined}
        >
          {unitKindLine(currentVehicle, {
            vehicle: t("vehicleCard.unitTypeVehicle"),
            trailer: t("vehicleCard.unitTypeTrailer"),
            device: t("vehicleCard.unitTypeDevice", "Dispositivo"),
          })}
        </span>

        {/* Campanita de la unidad: sus notificaciones en un popover, sin abrir nada más. */}
        <div className="ml-auto shrink-0 flex items-center gap-1">
            {/* Vista de manejo (beta): a la vista en el encabezado, no escondida en «Más»; abre sobre el mapa. */}
            {!isWebServiceRole && canDrawDriveView() && (
              <Tooltip content={t("vehicleCard.driveView", "Vista de manejo · Beta")} delay={150} position="top">
                <button
                  type="button"
                  aria-label={t("vehicleCard.driveView", "Vista de manejo · Beta")}
                  aria-pressed={driveViewOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDriveView(vehicle.id);
                    setFocusedVehicleId(vehicle.id);
                  }}
                  className={`relative w-7 h-7 flex items-center justify-center rounded-md border transition-[background-color,color,transform] duration-150 active:scale-[0.97] ${
                    driveViewOpen
                      ? "bg-primary-500 border-primary-500 text-gray-900"
                      : isDarkMode
                        ? "border-dark-border text-primary-500 hover:bg-dark-card"
                        : "border-gray-200 text-primary-600 hover:bg-gray-100"
                  }`}
                >
                  <i className="ri-gamepad-line text-base" aria-hidden />
                  {/* Novedad: palpita hasta la primera vez que se ve la vista. */}
                  {!driveViewTried && (
                    <span className="pointer-events-none absolute -top-1 -right-1 flex h-2.5 w-2.5" aria-hidden>
                      <span className="absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-70 motion-safe:animate-ping [animation-duration:1.6s]" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-500 ring-2 ring-black/40" />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}
            {!isWebServiceRole && (
              <UnitBell
                deviceId={vehicle.id}
                vehicleId={currentVehicle.vehicle_id}
                unitName={vehicle.vehicle?.name || vehicle.imei || vehicle.id}
                isDarkMode={isDarkMode}
                variant="card"
              />
            )}
            {isEvidenceAvailable && (
              <Tooltip content={t("vehicleCard.viewEvidence")} delay={0} position="top">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEvidenceModal(true);
                    setFocusedVehicleId(vehicle.id);
                  }}
                  className={`w-7 h-7 flex items-center justify-center rounded-md border transition-colors ${
                    isDarkMode
                      ? "border-dark-border text-cyan-400 hover:bg-dark-card"
                      : "border-gray-200 text-cyan-600 hover:bg-gray-100"
                  }`}
                >
                  <i className="ri-file-code-line text-base"></i>
                </button>
              </Tooltip>
            )}

            {isUserDev && (
              <Tooltip content={t("vehicleCard.viewLiveData")} delay={0} position="top">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openLiveDataPanel(vehicle.id);
                    setFocusedVehicleId(vehicle.id);
                  }}
                  className={`w-7 h-7 flex items-center justify-center rounded-md border transition-colors ${
                    isDarkMode
                      ? "border-dark-border text-primary-500 hover:bg-dark-card"
                      : "border-gray-200 text-primary-600 hover:bg-gray-100"
                  }`}
                >
                  <i className="ri-pulse-line text-base"></i>
                </button>
              </Tooltip>
            )}
        </div>
      </div>

      {/* Active trip badge with real-time tracking status */}
      {(activeTrip || (tripTracking && tripTracking.trip_status !== "COMPLETED")) && (() => {
        const isDeviated = tripTracking?.is_deviated ?? false;
        const trackingStatus = tripTracking?.trip_status;
        const lastEvent = tripTracking?.last_event;
        const isCompleted = trackingStatus === "COMPLETED";

        const hasPendingAssignment = activeTrip?.assignments?.some(
          (a) => a.status === "pending" && a.device_id === vehicle.id,
        );

        let badgeBg: string;
        let badgeBorder: string;
        let iconClass: string;
        let iconColor: string;
        let label: string;

        if (isCompleted && hasPendingAssignment) {
          badgeBg = isDarkMode ? "bg-yellow-500/10" : "bg-yellow-50";
          badgeBorder = isDarkMode ? "border-yellow-500/30" : "border-yellow-200";
          iconClass = "ri-time-line";
          iconColor = "text-yellow-500";
          label = t("vehicleCard.tripPending", "Viaje pendiente");
        } else if (isDeviated) {
          badgeBg = isDarkMode ? "bg-red-500/10" : "bg-red-50";
          badgeBorder = isDarkMode ? "border-red-500/30" : "border-red-200";
          iconClass = "ri-alert-line";
          iconColor = "text-red-500";
          label = lastEvent === "SIGUE_FUERA_DE_RUTA"
            ? t("vehicleCard.stillOffRoute", "Sigue fuera de ruta")
            : t("vehicleCard.offRoute", "Fuera de ruta");
        } else if (isCompleted) {
          badgeBg = isDarkMode ? "bg-green-500/10" : "bg-green-50";
          badgeBorder = isDarkMode ? "border-green-500/30" : "border-green-200";
          iconClass = "ri-checkbox-circle-line";
          iconColor = "text-green-500";
          label = t("vehicleCard.tripCompleted", "Viaje completado");
        } else if (trackingStatus === "IN_PROGRESS") {
          badgeBg = isDarkMode ? "bg-blue-500/10" : "bg-blue-50";
          badgeBorder = isDarkMode ? "border-blue-500/30" : "border-blue-200";
          iconClass = "ri-route-line";
          iconColor = "text-blue-500";
          label = lastEvent === "REGRESO_A_RUTA"
            ? t("vehicleCard.returnedToRoute", "Regresó a la ruta")
            : t("vehicleCard.activeTrip", "En ruta");
        } else if (hasPendingAssignment) {
          badgeBg = isDarkMode ? "bg-yellow-500/10" : "bg-yellow-50";
          badgeBorder = isDarkMode ? "border-yellow-500/30" : "border-yellow-200";
          iconClass = "ri-time-line";
          iconColor = "text-yellow-500";
          label = t("vehicleCard.tripPending", "Viaje pendiente");
        } else {
          badgeBg = isDarkMode ? "bg-blue-500/10" : "bg-blue-50";
          badgeBorder = isDarkMode ? "border-blue-500/30" : "border-blue-200";
          iconClass = "ri-route-line";
          iconColor = "text-blue-500";
          label = t("vehicleCard.activeTrip", "Viaje en curso");
        }

        const tripTitle = tripTracking?.trip_title || activeTrip?.title ||
          (activeTrip?.origin && activeTrip?.destiny
            ? `${activeTrip.origin} → ${activeTrip.destiny}`
            : activeTrip?.id || tripTracking?.route_name || "");

        return (
          <div
            className={`flex items-center gap-2 mb-3 px-2 py-1.5 rounded-md border ${badgeBg} ${badgeBorder}`}
            title={tripTracking?.message || tripTitle}
          >
            <i className={`${iconClass} ${iconColor} text-sm flex-shrink-0`}></i>
            <div className="flex flex-col min-w-0 flex-1">
              <span className={`text-[10px] font-semibold uppercase tracking-wide ${iconColor} leading-none`}>
                {label}
              </span>
              <span className={`text-xs font-medium truncate ${themeClasses.text}`}>
                {tripTitle}
              </span>
            </div>
            {tripEvents.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTripHistory(true);
                }}
                className={`flex-shrink-0 text-[10px] font-medium ${iconColor} hover:underline flex items-center gap-0.5`}
              >
                {t("vehicleCard.viewMore")}
                <i className="ri-arrow-right-s-line"></i>
              </button>
            )}
          </div>
        );
      })()}

      {showTripHistory && (
        <TripHistoryModal
          vehicle={currentVehicle}
          events={tripEvents}
          isDarkMode={isDarkMode}
          onClose={() => setShowTripHistory(false)}
        />
      )}

      {showBitacora && (
        <BitacoraDrawer
          deviceId={vehicle.id}
          unitName={vehicle.vehicle?.name || vehicle.imei || vehicle.id}
          plate={vehicle.vehicle?.plates}
          isDarkMode={isDarkMode}
          onClose={() => setShowBitacora(false)}
          // "Asignar conductor" reutiliza la acción Conductor de esta tarjeta.
          onAssignDriver={
            isWebServiceRole
              ? null
              : () => {
                  setShowBitacora(false);
                  if (activeActionIcon !== "driver") handleActionIconClick("driver");
                }
          }
        />
      )}

      {showEvidenceModal && (
        <VehicleEvidenceModal
          vehicle={currentVehicle}
          isDarkMode={isDarkMode}
          onClose={() => setShowEvidenceModal(false)}
        />
      )}

      {isLiveDataOpen && (
        <VehicleLiveDataModal
          vehicle={currentVehicle}
          isDarkMode={isDarkMode}
          onClose={closeLiveDataPanel}
        />
      )}

      {/* Iconos Grandes con colores basados en estado */}
      <div className="flex items-center justify-evenly gap-0.5 mt-3 mb-3">
        {/* 1. Panic (SOS) */}
        <Tooltip content={t("vehicleCard.panic") || "Pánico/SOS"} delay={0} position="top">
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className={`border-b-2 ${panicBorderColor} pb-2 flex items-center justify-center h-6`}>
              <SosIcon color={panicColor} className="max-w-full h-auto max-h-4" />
            </div>
            <span className={`text-[10px] mt-1 ${getTextColorClass(panicColor)}`}>
              {currentVehicle.params.panic == null ? t("vehicleCard.noData") :
                isPanicCode(currentVehicle.params.panic) ? t("vehicleCard.statusOn") : t("vehicleCard.statusOff")}
            </span>
          </div>
        </Tooltip>

        {/* 2. Engine Status (Ignition) - Lógica: speed > 0 → ON, movimiento real → ON, sino → engine_status */}
        <Tooltip
          content={
            engineDisplayInfo.stale && engineDisplayInfo.lastKnownOn !== null
              ? `${t("vehicleCard.ignition")} · ${t("vehicleCard.lastKnown")}: ${
                  engineDisplayInfo.lastKnownOn ? t("vehicleCard.statusOn") : t("vehicleCard.statusOff")
                }`
              : t("vehicleCard.ignition") || "Ignición"
          }
          delay={0}
          position="top"
        >
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className={`border-b-2 ${engineDisplayInfo.borderColor} pb-2 flex items-center justify-center h-6`}>
              <IgnitionIcon color={engineDisplayInfo.color} className="max-w-full h-auto max-h-4" />
            </div>
            <span className={`text-[10px] mt-1 ${getTextColorClass(engineDisplayInfo.color)}`}>
              {engineDisplayInfo.isOn === true ? t("vehicleCard.statusOn") :
                engineDisplayInfo.isOn === false ? t("vehicleCard.statusOff") : t("vehicleCard.noData")}
            </span>
          </div>
        </Tooltip>

        {/* 3. Battery */}
        <Tooltip content={t("vehicleCard.battery") || "Batería"} delay={0} position="top">
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className={`border-b-2 ${batteryBorderColor} pb-2 flex items-center justify-center h-6`}>
              <BatteryIcon color={batteryColor} className="max-w-full h-auto max-h-4" />
            </div>
            <span
              className={`text-[10px] mt-1 ${getTextColorClass(batteryColor)}`}
            >
              {currentVehicle.battery != null
                ? `${currentVehicle.battery.toFixed(0)}%`
                : t("vehicleCard.noData")}
            </span>
          </div>
        </Tooltip>

        {/* 4. GPS Sat (Signal) */}
        <Tooltip content={t("vehicleCard.gpsSignal") || "Señal GPS"} delay={0} position="top">
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className={`border-b-2 ${gpsSatBorderColor} pb-2 flex items-center justify-center h-6`}>
              <SignIcon color={gpsSatColor} className="max-w-full h-auto max-h-4" />
            </div>
            <span
              className={`text-[10px] mt-1 ${getTextColorClass(gpsSatColor)}`}
            >
              {currentVehicle.gps_sat ?? "-"}
            </span>
          </div>
        </Tooltip>

        {/* 5. Last Connection */}
        <Tooltip content={t("vehicleCard.lastConnection") || "Última conexión"} delay={0} position="top">
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className={`border-b-2 ${lastConnectionBorderColor} pb-2 flex items-center justify-center h-6`}>
              <LastReportIcon color={lastConnectionColor} className="max-w-full h-auto max-h-4" />
            </div>
            <span
              className={`text-[10px] mt-1 truncate w-full text-center ${getTextColorClass(lastConnectionColor)}`}
              title={lastConnectionText}
            >
              {lastConnectionShort}
            </span>
          </div>
        </Tooltip>

        {/* 6. Odometer */}
        <Tooltip content={t("vehicleCard.odometer") || "Odómetro"} delay={0} position="top">
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className="border-b-2 border-gray-500 pb-2 flex items-center justify-center h-6">
              <OdometerIcon color={GRAY_COLOR} className="max-w-full h-auto max-h-4" />
            </div>
            <span className={`text-[10px] mt-1 ${themeClasses.textSecondary}`}>
              {odometerText}
            </span>
          </div>
        </Tooltip>

        {/* 7. Speed */}
        <Tooltip
          content={
            liveSpeed == null && currentVehicle.speed != null
              ? `${t("vehicleCard.speed")} · ${t("vehicleCard.lastKnown")}: ${Math.round(currentVehicle.speed)} km/h`
              : t("vehicleCard.speed") || "Velocidad"
          }
          delay={0}
          position="top"
        >
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className="border-b-2 border-gray-500 pb-2 flex items-center justify-center h-6">
              <SpeedIcon color={GRAY_COLOR} className="max-w-full h-auto max-h-4" />
            </div>
            <span className={`text-[10px] mt-1 ${themeClasses.textSecondary}`}>
              {liveSpeed != null ? `${Math.round(liveSpeed)} km/h` : t("vehicleCard.noData")}
            </span>
          </div>
        </Tooltip>

        {/* 8. Paro de motor */}
        {isEngineCutoffBrand && (
          <Tooltip content={t("vehicleCard.engineCutoff") || "Paro de motor"} delay={0} position="top">
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className={`border-b-2 ${currentVehicle.engineCutoff ? "border-[#EF4444]" : "border-gray-500"} pb-2 flex items-center justify-center h-6`}>
                <span className="relative inline-flex items-center justify-center">
                  <i
                    className={`${currentVehicle.engineCutoff ? "ri-car-fill" : "ri-car-line"} text-base leading-none`}
                    style={{ color: currentVehicle.engineCutoff ? RED_COLOR : GRAY_COLOR }}
                  ></i>
                  {currentVehicle.engineCutoff && (
                    <span
                      className="absolute left-1/2 top-1/2 w-[18px] h-[2px] rounded-full pointer-events-none"
                      style={{ backgroundColor: RED_COLOR, transform: "translate(-50%, -50%) rotate(-18deg)" }}
                    />
                  )}
                </span>
              </div>
              <span className={`text-[10px] mt-1 truncate w-full text-center ${currentVehicle.engineCutoff ? "text-[#EF4444]" : themeClasses.textSecondary}`}>
                {currentVehicle.engineCutoff == null
                  ? t("vehicleCard.noData")
                  : currentVehicle.engineCutoff
                    ? t("vehicleCard.engineCutoffActive")
                    : t("vehicleCard.engineCutoffInactive")}
              </span>
            </div>
          </Tooltip>
        )}

        {/* 9. Enganche */}
        {isHitchModel && (
          <Tooltip content={t("vehicleCard.hitch") || "Enganche"} delay={0} position="top">
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className={`border-b-2 ${currentVehicle.hitch ? "border-[#22C55E]" : "border-gray-500"} pb-2 flex items-center justify-center h-6`}>
                <i
                  className={`${currentVehicle.hitch ? "ri-link" : "ri-link-unlink"} text-base leading-none`}
                  style={{ color: currentVehicle.hitch ? GREEN_COLOR : GRAY_COLOR }}
                ></i>
              </div>
              <span className={`text-[10px] mt-1 truncate w-full text-center ${currentVehicle.hitch ? "text-[#22C55E]" : themeClasses.textSecondary}`}>
                {currentVehicle.hitch == null
                  ? t("vehicleCard.noData")
                  : currentVehicle.hitch
                    ? t("vehicleCard.hitched")
                    : t("vehicleCard.unhitched")}
              </span>
            </div>
          </Tooltip>
        )}
      </div>

      {/* «Hoy» (la pestaña «Ahora» de Mapon): la calificación de conducción
          (A-G, la del informe «Comportamiento del conductor»; el clic lleva al
          informe) y distancia, conduciendo, ralentí y detenido del mismo
          cálculo. Sólo en la unidad seleccionada. Mientras carga se ve ya
          con su forma final y una línea que barre arriba: así no salta. */}
      {isSelected && (todayScore.data || todayScore.isPending) && (() => {
        const data = todayScore.data;
        const d = data ? todaySummary(data, startOfToday(), Date.now()) : null;
        const stats = [
          { label: t("vehicleCard.todayDistance", "Distancia"), value: d ? fmtKm(d.km) : "—" },
          { label: t("vehicleCard.todayDriving", "Conduciendo"), value: d ? fmtDuration(d.drivingMs) : "—" },
          { label: t("vehicleCard.todayIdle", "Ralentí"), value: d ? fmtDuration(d.idleMs) : "—" },
          { label: t("vehicleCard.todayStopped", "Detenido"), value: d ? fmtDuration(d.stoppedMs) : "—" },
        ];
        return (
          <div
            aria-label={t("vehicleCard.today", "Hoy")}
            aria-busy={!data}
            className={`relative overflow-hidden mx-1 mb-2 rounded-lg border ${isDarkMode ? "border-dark-border bg-white/[0.03]" : "border-gray-200 bg-gray-50"}`}
          >
            {!data && (
              <span className="absolute inset-x-0 top-0 h-0.5 overflow-hidden" aria-hidden="true">
                <span className="block h-full w-2/5 bg-primary-500 rounded-full animate-lineSweep motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-40" />
              </span>
            )}
            <Tooltip content={t("vehicleCard.drivingSeeReport", "Calificación de hoy · Ver comportamiento del conductor")} delay={0} position="top">
              <button
                type="button"
                onClick={() => navigate("/reports/behavior")}
                aria-label={t("vehicleCard.drivingToday", "Conducción hoy")}
                className={`group w-full flex items-center gap-2.5 px-2 py-1.5 rounded-t-lg border-b transition-colors duration-150 ${isDarkMode ? "border-dark-border hover:bg-white/[0.05]" : "border-gray-200 hover:bg-gray-100"}`}
              >
                <span
                  className="w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-sm font-bold leading-none"
                  style={{ color: TONE_COLOR[todayTone], backgroundColor: `color-mix(in srgb, ${TONE_COLOR[todayTone]} 16%, transparent)` }}
                >
                  {data?.overall == null ? "–" : todayGrade}
                </span>
                <span className={`text-[11px] ${themeClasses.textSecondary}`}>{t("vehicleCard.drivingToday", "Conducción hoy")}</span>
                <span className={`ml-auto text-xs font-semibold tabular-nums ${themeClasses.text}`}>
                  {!data ? "—" : data.overall == null ? t("vehicleCard.drivingNoTrips", "Sin manejo") : `${Math.round(data.overall)} / 100`}
                </span>
                <i className={`ri-arrow-right-s-line text-sm transition-transform duration-150 group-hover:translate-x-0.5 ${themeClasses.textSecondary}`} aria-hidden="true"></i>
              </button>
            </Tooltip>
            <div className="grid grid-cols-4">
              {stats.map((x, i) => (
                <div key={x.label} className={`px-2 py-1.5 text-center min-w-0 ${i > 0 ? (isDarkMode ? "border-l border-dark-border" : "border-l border-gray-200") : ""}`}>
                  <p className={`text-xs font-semibold tabular-nums truncate ${themeClasses.text}`}>{x.value}</p>
                  <p className={`text-[10px] truncate ${themeClasses.textSecondary}`}>{x.label}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Acciones. Las cuatro que más se usan van con NOMBRE, al estilo de
          Mapon (la acción vive en la unidad y se lee sin adivinar el ícono);
          las otras siete siguen ahí, detrás de "Más". Antes eran 11 íconos sin
          texto en fila: había que pasar el cursor por cada uno para saber qué
          hacía. La lista es una sola para que primarias y "Más" no dupliquen
          lógica. */}
      {(() => {
        const offline = currentVehicle.status === "offline";
        type Action = {
          id: ActionIconType;
          label: string;
          tooltip?: string;
          Icon: (props: { color?: string }) => React.ReactElement;
          onClick: () => void;
          disabled?: boolean;
          iconColor?: string;
        };
        const actions: Action[] = [
          { id: "route", label: t("quickActions.routeToday", "Ruta de hoy"), tooltip: t("vehicleCard.route"), Icon: RouteIcon, onClick: () => handleActionIconClick("route"), disabled: isWebServiceRole },
          { id: "events", label: t("vehicleCard.events"), Icon: EventsIcon, onClick: () => handleActionIconClick("events"), disabled: isWebServiceRole },
          { id: "share", label: t("quickActions.share", "Compartir"), tooltip: t("vehicleCard.shareTracking"), Icon: ShareIcon, onClick: () => handleActionIconClick("share"), disabled: isWebServiceRole },
          { id: "settings", label: t("quickActions.commands", "Comandos"), tooltip: t("vehicleCard.settings"), Icon: SettingsIcon, onClick: () => handleActionIconClick("settings"), disabled: isWebServiceRole },
          { id: "drivingEvents", label: t("vehicleCard.drivingEvents", "Eventos de conducción"), Icon: DrivingEventsIcon, onClick: () => setShowBitacora(true), disabled: isWebServiceRole },
          { id: "generalInformation", label: t("vehicleCard.generalInformation"), Icon: GeneralInformationIcon, onClick: () => { handleActionIconClick("generalInformation"); requestAddressForDevice(vehicle.id); } },
          { id: "driver", label: t("vehicleCard.driver"), Icon: DriverIcon, onClick: () => handleActionIconClick("driver"), disabled: isWebServiceRole },
          { id: "trailer", label: t("vehicleCard.trailer"), Icon: TrailerIcon, onClick: () => handleActionIconClick("trailer"), disabled: isWebServiceRole },
          { id: "states", label: t("vehicleCard.states"), Icon: StatesIcon, onClick: () => handleActionIconClick("states"), disabled: isWebServiceRole },
          { id: "units", label: t("vehicleCard.units"), Icon: ProfilteIcon, onClick: () => handleActionIconClick("units"), disabled: isWebServiceRole },
          { id: "footprint", label: t("vehicleCard.footprint"), Icon: FootprintUnitIcon, onClick: () => handleActionIconClick("footprint"), disabled: isWebServiceRole },
          {
            id: "record",
            label: t("vehicleCard.recordRoute") || "Grabar recorrido",
            tooltip: offline ? t("vehicleCard.recordNoGps") || "Este vehículo está offline y no puede ser grabado" : undefined,
            Icon: RecorderIcon,
            onClick: () => !offline && handleActionIconClick("record"),
            disabled: isWebServiceRole || offline,
            iconColor: activeActionIcon === "record" ? "#EF4444" : undefined,
          },
        ];
        const primary = actions.slice(0, 4);
        const secondary = actions.slice(4);
        // "Más" se abre solo si la acción activa es secundaria, para que no
        // quede abierta una acción que no se ve en la fila.
        const moreOpen = showMoreActions || secondary.some((x) => x.id === activeActionIcon);
        const chip = (x: Action) => (
          <Tooltip key={x.id} content={x.tooltip || x.label} delay={150} position="top" className="!w-auto">
            <button
              type="button"
              disabled={x.disabled}
              onClick={(e) => { e.stopPropagation(); x.onClick(); }}
              onMouseEnter={() => setHoveredIcon(x.id)}
              onMouseLeave={() => setHoveredIcon(null)}
              className={`h-8 px-2 flex items-center gap-1 rounded-md border text-xs font-medium transition-colors active:translate-y-px disabled:opacity-40 disabled:cursor-not-allowed ${
                activeActionIcon === x.id
                  ? "bg-primary-500 border-primary-500 text-gray-900"
                  : `${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-full">
                <x.Icon color={x.iconColor || getIconColor(x.id)} />
              </span>
              <span className="whitespace-nowrap">{x.label}</span>
            </button>
          </Tooltip>
        );
        return (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              {primary.map(chip)}
              {/* Copiar ubicación: en la plataforma anterior estaba a la vista
                  en cada tarjeta y era de lo más usado (se pega en WhatsApp).
                  No abre panel, así que no es una acción de la lista: confirma
                  en el mismo botón. */}
              <Tooltip
                content={
                  copyLocation.available
                    ? t("quickActions.copyLocationTip", "Copiar link de Google Maps")
                    : t("quickActions.noLocation", "Sin ubicación para copiar")
                }
                delay={150}
                position="top"
                className="!w-auto"
              >
                <button
                  type="button"
                  disabled={!copyLocation.available}
                  onClick={(e) => { e.stopPropagation(); void copyLocation.copy(); }}
                  aria-live="polite"
                  className={`h-8 px-2 flex items-center gap-1 rounded-md border text-xs font-medium transition-colors active:translate-y-px disabled:opacity-40 disabled:cursor-not-allowed ${
                    copyLocation.state === "copied"
                      ? "border-primary-500 text-primary-500"
                      : copyLocation.state === "failed"
                        ? "border-red-400 text-red-400"
                        : `${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                  }`}
                >
                  <i
                    className={`${
                      copyLocation.state === "copied"
                        ? "ri-check-line"
                        : copyLocation.state === "failed"
                          ? "ri-error-warning-line"
                          : "ri-file-copy-line"
                    } text-sm leading-none`}
                  />
                  <span className="whitespace-nowrap">
                    {copyLocation.state === "copied"
                      ? t("quickActions.copied", "Copiada")
                      : copyLocation.state === "failed"
                        ? t("quickActions.copyFailed", "No se pudo copiar")
                        : t("quickActions.copyLocation", "Ubicación")}
                  </span>
                </button>
              </Tooltip>
              {/* "Más" sólo con ícono: con texto no cabía en la misma fila que las
                  cuatro acciones en el panel de escritorio (~500 px). */}
              <Tooltip
                content={moreOpen ? t("quickActions.less", "Menos") : t("quickActions.more", "Más acciones")}
                delay={150}
                position="top"
                className="!w-auto"
              >
                <button
                  type="button"
                  aria-expanded={moreOpen}
                  aria-label={moreOpen ? t("quickActions.less", "Menos") : t("quickActions.more", "Más acciones")}
                  onClick={(e) => { e.stopPropagation(); setShowMoreActions((v) => !v); }}
                  className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors ${themeClasses.cardBorder} ${themeClasses.textSecondary} ${themeClasses.buttonHover}`}
                >
                  <i className={`${moreOpen ? "ri-arrow-up-s-line" : "ri-more-fill"} text-base leading-none`} />
                </button>
              </Tooltip>
            </div>
            {moreOpen && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{secondary.map(chip)}</div>}
          </>
        );
      })()}

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: activeActionIcon ? `${contentHeight}px` : "0px",
          opacity: activeActionIcon ? 1 : 0,
        }}
      >
        <div ref={contentRef}>{renderOptionContent()}</div>
      </div>
    </div>
  );
}

const VehicleCard = memo(VehicleCardComponent, (prevProps, nextProps) => {
  // Solo re-renderizar si cambian propiedades relevantes
  return (
    prevProps.vehicle.id === nextProps.vehicle.id &&
    prevProps.vehicle.status === nextProps.vehicle.status &&
    prevProps.vehicle.latitude === nextProps.vehicle.latitude &&
    prevProps.vehicle.longitude === nextProps.vehicle.longitude &&
    prevProps.vehicle.speed === nextProps.vehicle.speed &&
    prevProps.vehicle.battery === nextProps.vehicle.battery &&
    prevProps.vehicle.engineStatus === nextProps.vehicle.engineStatus &&
    prevProps.vehicle.gps_sat === nextProps.vehicle.gps_sat &&
    prevProps.vehicle.lastReport._seconds ===
    nextProps.vehicle.lastReport._seconds &&
    prevProps.vehicle.odometer === nextProps.vehicle.odometer &&
    prevProps.vehicle.eventCode === nextProps.vehicle.eventCode &&
    prevProps.vehicle.params?.panic === nextProps.vehicle.params?.panic &&
    prevProps.vehicle.engineCutoff === nextProps.vehicle.engineCutoff &&
    prevProps.vehicle.hitch === nextProps.vehicle.hitch &&
    prevProps.vehicle.brandModel === nextProps.vehicle.brandModel &&
    prevProps.vehicle.brandName === nextProps.vehicle.brandName &&
    prevProps.isDarkMode === nextProps.isDarkMode &&
    prevProps.selectedStatus === nextProps.selectedStatus
  );
});

export default VehicleCard;
